import { useState, ChangeEvent } from "react";
import Button from "@/common/components/Button";
import GoBackButton from "@/common/components/GoBackButton";
import Modal from "@/common/components/Modal";
import TextArea from "@/common/components/TextArea";
import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper";
import { MissionDetailsView } from "../../components/MissionDetailsView";
import { useMission } from "../../hooks/useMissions";
import { useDisableMission } from "../../hooks/useDisableMission";
import { useParams, Link } from "react-router-dom";
import { ChevronsRight } from "lucide-react";
import { useGoBack } from "@/common/hooks/useGoBack";
import useUserPermissions from "@/common/hooks/useUserPermissions";

const MissionsShow = () => {
  const { id } = useParams<{ id: string }>();
  const goBack = useGoBack();
  const { hasUserPermission } = useUserPermissions();
  const [isDisableModalOpen, setIsDisableModalOpen] = useState(false);
  const [disableReason, setDisableReason] = useState("");
  const [disableReasonTouched, setDisableReasonTouched] = useState(false);
  const canUpdateMission = hasUserPermission(
    "Establishment::Tmais::Missions::Update",
  );
  const canDisableMission = hasUserPermission(
    "Establishment::Tmais::Missions::Disable",
  );

  const { data: mission, isLoading, error, refetch } = useMission(id);
  const { mutate: disableMission, isPending: isDeleting } = useDisableMission({
    onSuccess: () => {
      setIsDisableModalOpen(false);
      setDisableReason("");
      setDisableReasonTouched(false);
      goBack({ fallback: "/tmais-missions" });
    },
  });
  const editButton = (
    <Button
      variant="default"
      disabled={isDeleting}
      operationKlassToCheckPermission="Establishment::Tmais::Missions::Update"
    >
      Editar
    </Button>
  );

  const trimmedDisableReason = disableReason.trim();
  const isDisableReasonValid = trimmedDisableReason.length >= 3;
  const disableReasonError =
    disableReasonTouched && !isDisableReasonValid
      ? "Informe pelo menos 3 caracteres."
      : undefined;

  const handleOpenDisableModal = () => {
    if (mission?.status !== "active" || !canDisableMission) return;
    setDisableReason("");
    setDisableReasonTouched(false);
    setIsDisableModalOpen(true);
  };

  const handleCloseDisableModal = () => {
    if (isDeleting) return;
    setIsDisableModalOpen(false);
    setDisableReason("");
    setDisableReasonTouched(false);
  };

  const handleDisableModalOpenChange = (open: boolean) => {
    if (open) {
      setIsDisableModalOpen(true);
      return;
    }
    handleCloseDisableModal();
  };

  const handleConfirmDisable = () => {
    setDisableReasonTouched(true);
    if (!id || !isDisableReasonValid || !canDisableMission) return;
    disableMission({ id, reason: trimmedDisableReason });
  };

  if (isLoading) {
    return (
      <ErrorMessageWrapper className="h-[calc(100vh-100px)]" statusCode={200}>
        <div className="flex h-full items-center justify-center text-gray-400">
          Carregando...
        </div>
      </ErrorMessageWrapper>
    );
  }

  return (
    <ErrorMessageWrapper
      statusCode={(error as any)?.status || (mission ? 200 : 404)}
      refetchFunction={refetch}
      notFoundFallback="Missão não encontrada"
      notFoundHideButton
    >
      {mission && (
        <div className="flex flex-col h-[calc(100vh-100px)]">
          <div className="flex justify-between items-center mb-6 shrink-0">
            <div className="flex gap-2 items-center text-[20px]">
              <GoBackButton fallback={`/tmais-missions`} />
              <p className="text-gray-300 text-base font-semibold tracking-wide">
                Missões
              </p>
              <ChevronsRight className="text-gray-400 w-5 h-5" />
              <h2 className="text-base font-bold">Visualizar missão</h2>
            </div>

            <div className="flex gap-4">
              {mission.status === "active" && (
                <Button
                  variant="error"
                  onClick={handleOpenDisableModal}
                  isLoading={isDeleting}
                  disabled={isDeleting}
                  operationKlassToCheckPermission="Establishment::Tmais::Missions::Disable"
                >
                  Desativar
                </Button>
              )}
              {canUpdateMission ? (
                <Link to={`/tmais-missions/${mission.id}/edit`}>
                  {editButton}
                </Link>
              ) : (
                editButton
              )}
            </div>
          </div>
          <div className="flex-1 overflow-y-auto pr-4 pb-10">
            <MissionDetailsView mission={mission} />
          </div>

          <Modal
            title="Desativar missão"
            subtitle="Descreva o motivo da desativação. O texto ficará registrado no histórico da missão."
            useCancellableFooter
            confirmButtonText="Desativar"
            cancelButtonText="Cancelar"
            confirmButtonIsLoading={isDeleting}
            confirmButtonDisabled={!isDisableReasonValid}
            onConfirm={handleConfirmDisable}
            onCancel={handleCloseDisableModal}
            isOpen={isDisableModalOpen}
            setIsOpen={handleDisableModalOpenChange}
            body={
              <div className="flex flex-col gap-3 text-white">
                <TextArea
                  label="Motivo"
                  placeholder="Ex.: Missão encerrada pela matriz"
                  value={disableReason}
                  onChange={(event: ChangeEvent<HTMLTextAreaElement>) => {
                    if (!disableReasonTouched) setDisableReasonTouched(true);
                    setDisableReason(event.target.value);
                  }}
                  rows={4}
                  maxLength={500}
                  markAsRequired
                  errorMessage={disableReasonError}
                />
              </div>
            }
          />
        </div>
      )}
    </ErrorMessageWrapper>
  );
};

export default MissionsShow;
